import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Alert, Badge, Spinner, UncontrolledTooltip } from "reactstrap";

import {
  getPermissionsForRole,
  isOwnerOnlyPermission,
  permissionTooltipKey,
} from "../../../helpers/teamPermissionUtils";
import HelpArticleLink from "../../Common/HelpArticleLink";
import { HELP_ARTICLE_TEAM_PERMISSIONS } from "../../../helpers/helpCenter";
import { TeamPermissionMatrix } from "../../../services/permissionService";

interface RolePermissionsSummaryProps {
  matrix: TeamPermissionMatrix | null;
  roleName: string;
  loading?: boolean;
  loadError?: boolean;
  highlighted?: boolean;
}

const RolePermissionsSummary: React.FC<RolePermissionsSummaryProps> = ({
  matrix,
  roleName,
  loading = false,
  loadError = false,
  highlighted = true,
}) => {
  const { t } = useTranslation();

  const permissions = useMemo(() => {
    if (!matrix || !roleName) {
      return [];
    }

    return getPermissionsForRole(matrix, roleName);
  }, [matrix, roleName]);

  if (!roleName) {
    return null;
  }

  return (
    <div
      className={`rounded border p-3 mb-3 ${
        highlighted ? "bg-light border-primary border-opacity-25" : ""
      }`}
    >
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-2">
        <div>
          <h6 className="mb-1">{t("users.form.permissions_summary.title")}</h6>
          <p className="text-muted small mb-0">
            {t("users.form.permissions_summary.subtitle", {
              role: t(`users.fields.roles.options.${roleName}`, { defaultValue: roleName }),
            })}
          </p>
        </div>
        <Link to="/settings/team-permissions" className="small fw-medium">
          {t("users.form.permissions_summary.learnProfilesLink")}
        </Link>
      </div>

      {loading ? (
        <div className="py-2">
          <Spinner size="sm" color="primary" className="me-2" />
          {t("users.form.permissions_summary.loading")}
        </div>
      ) : loadError ? (
        <Alert color="warning" className="mb-0 py-2">
          {t("users.form.permissions_summary.loadError")}
        </Alert>
      ) : permissions.length === 0 ? (
        <p className="text-muted small mb-0">{t("users.form.permissions_summary.empty")}</p>
      ) : (
        <ul className="list-unstyled mb-0">
          {permissions.map((permission) => {
            const labelId = `role-perm-${permissionTooltipKey(permission.name)}`;
            const tooltipKey = `team_permissions.tooltips.${permissionTooltipKey(permission.name)}`;
            const tooltipText = t(tooltipKey, { defaultValue: "" });
            const ownerOnly = isOwnerOnlyPermission(permission);

            return (
              <li key={permission.name} className="d-flex align-items-start gap-2 py-1">
                <i className="ri-checkbox-circle-fill text-success mt-1" aria-hidden="true" />
                <span className="flex-grow-1">
                  <span id={tooltipText ? labelId : undefined}>{permission.display_name}</span>
                  {ownerOnly ? (
                    <Badge color="soft-warning" className="ms-2 align-middle">
                      {t("team_permissions.badges.ownerOnly")}
                    </Badge>
                  ) : null}
                  {tooltipText ? (
                    <>
                      <i
                        className="ri-information-line text-muted ms-1 align-middle"
                        id={`${labelId}-icon`}
                        aria-hidden="true"
                      />
                      <UncontrolledTooltip placement="top" target={labelId}>
                        {tooltipText}
                      </UncontrolledTooltip>
                      <UncontrolledTooltip placement="top" target={`${labelId}-icon`}>
                        {tooltipText}
                      </UncontrolledTooltip>
                    </>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-muted small mb-0 mt-2">
        <HelpArticleLink
          articlePath={HELP_ARTICLE_TEAM_PERMISSIONS}
          className="text-muted"
          label={t("users.form.permissions_summary.helpLink")}
        />
      </p>
    </div>
  );
};

export default RolePermissionsSummary;
